
import React, { createContext, useContext, useState, useEffect } from 'react';

export const DataContext = createContext();
export const useDataContext = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
    const [apiData, setApiData] = useState(null);
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);

    const updateApiData = (data) => {
        setApiData(data);
    };

    // Helper function to load auth state from localStorage
    const loadAuthState = () => {
        setLoading(true);

        try {
            const userData = localStorage.getItem('User');
            const authentication = localStorage.getItem('Authentication');
            
            if (userData && authentication) {
                const parsedUser = JSON.parse(userData);
                const parsedAuth = JSON.parse(authentication);
                
                setUser(parsedUser);
                setIsAuthenticated(true);
                setUserRole(parsedUser?.role || null);
                
                // Load permissions from localStorage if available
                const permissionsData = localStorage.getItem('Permissions');
                if (permissionsData) {
                    setPermissions(JSON.parse(permissionsData));
                } else {
                    setPermissions(parsedUser?.permissions || []);
                }
            } else {
                // Clear auth state if localStorage doesn't have valid data
                setUser(null);
                setIsAuthenticated(false);
                setUserRole(null);
                setPermissions([]);
            }
        } catch (e) {
            console.error('Error loading auth state:', e);
            setUser(null);
            setIsAuthenticated(false);
            setUserRole(null);
            setPermissions([]);
        } finally {
            setLoading(false);
        }
    };

    // Initialize authentication state from localStorage on mount
    useEffect(() => {
        loadAuthState();

        // Listen for storage changes from other tabs or logout calls
        const handleStorageChange = (e) => {
            if (e.key === 'User' || e.key === 'Authentication' || e.key === null) {
                loadAuthState();
            }
        };

        // Listen for custom logout event
        const handleLogoutEvent = () => {
            setUser(null);
            setIsAuthenticated(false);
            setUserRole(null);
            setPermissions([]);
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('logout', handleLogoutEvent);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('logout', handleLogoutEvent);
        };
    }, []);

    const value = {
        apiData,
        updateApiData,
        user,
        setUser,
        isAuthenticated,
        setIsAuthenticated,
        userRole,
        setUserRole,
        permissions,
        setPermissions,
        loading,
        setLoading,
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};
